import { Fragment, memo, useState } from "react";
import COLORS from "../../theme/Colors";
import {
    Container,
    InputFile,
    StyledUploadFileIcon,
    InputFileButtonContainer,
    RowContainer,
    RowSeparator,
    StyledSearchIcon,
    PaginationContainer,
    DropZoneContainer,
    DropZoneTitle,
    DropZoneSubtitle,
    FileCardContainer,
    FileCardInfo,
    FileCardName,
    FileCardSize,
    FileCardActionWrapper
} from "./styles/UploadFileStyles";
import { FieldTitle, Row, SmallHeightDivider, StyledButton, StyledPagination, StyledTextInput } from "../../theme/Styles";
import FormModal from "../FormModal/FormModal";
import DocumentsOfRequestsCard from "../DocumentsOfRequestsCard/DocumentsOfRequestsCard";
import { format } from "date-fns";
import { useQuery, useQueryClient } from "react-query";
import { getPersonalDocuments } from "../../api/MyDocuments";
import { cacheConfig } from "../../cacheConfig";
import { types, typesForSelectedList, typesForExcel } from "./UploadFileConstants";
import { useSnackbar } from "notistack";
import { FormControl, FormGroup, FormHelperText, IconButton, Link } from "@mui/material";
import { replaceGuionToSlashFromString } from "../../utilities/functions/StringUtil";
import { getRandomUUIDV4 } from "../../utilities/functions/NumberUtil";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';

const getFileIcon = (fileName) => {
    if (!fileName) return <DescriptionIcon sx={{ color: '#64748B' }} />;
    const ext = fileName.split('.').pop().toLowerCase();
    if (ext === 'pdf') {
        return <PictureAsPdfIcon sx={{ color: '#EF4444' }} />;
    }
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) {
        return <ImageIcon sx={{ color: '#3B82F6' }} />;
    }
    return <DescriptionIcon sx={{ color: '#64748B' }} />;
};

function UploadFile({
    id,
    title,
    placeholder,
    onChange,
    value,
    onBlur,
    disabled,
    error,
    required,
    hideDownloadButton,
    extension,
    helperText = " ",
    findDocuments = false,
    multipleDocuments = false,
    ignoreType = [],
    maxFileSize = 10,
}) {
    const queryClient = useQueryClient();
    const userData = queryClient.getQueryData(["userData"]);
    const { enqueueSnackbar } = useSnackbar();

    const [currentPage, setCurrentPage] = useState(1);

    const { data: documentsData, isLoading: documentsDataLoading } = useQuery(
        ["documentsData", currentPage],
        () => getPersonalDocuments(userData.payload.citizen_id, currentPage),
        {
            staleTime: cacheConfig.staleTimeForRequestedServiceDetail,
            enabled: findDocuments === true,
        }
    );

    const [selectedFilesModalIsOpen, setSelectedFilesModalIsOpen] = useState(false);
    const [myDocumentsModalIsOpen, setMyDocumentsModalIsOpen] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState(value?.files ? value.files : []);
    const [selectedFileName, setSelectedFileName] = useState(value?.files?.[0]?.name);
    const [selectedItemsByIndexInDocuments, setSelectedItemsByIndexInDocuments] = useState(
        value?.selectedDocumentsArray ? value?.selectedDocumentsArray : []
    );

    const [isDragging, setIsDragging] = useState(false);

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled) return;
        setIsDragging(true);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled) return;
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (disabled) return;

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const mockEvent = {
                target: {
                    files: e.dataTransfer.files
                }
            };
            validateAndChangeSelectedFiles(onChange, mockEvent);
        }
    };

    const handleDropZoneClick = () => {
        if (disabled) return;
        document.getElementById(id)?.click();
    };

    const validateAndChangeSelectedFiles = (action, e) => {
        if (multipleDocuments === false && e.target.files.length > 1) {
            enqueueSnackbar("No se puede agregar mas de un documento", { variant: "error" });
            return;
        }
        let filesLength = e.target.files.length;
        if (filesLength > 0) {
            let loopError = false;
            let data = [];
            for (let i = 0; i < filesLength; i++) {
                let file = e.target.files[i];
                file.id = getRandomUUIDV4();
                const fileExtension =
                    e.target.files[i].name.substring(e.target.files[i].name.lastIndexOf(".") + 1, e.target.files[i].name.length).toLowerCase() ||
                    e.target.files[i].name.toLowerCase();
                const fileSize = file.size / 1024 / 1024;

                if (fileSize > maxFileSize) {
                    enqueueSnackbar(`El peso límite por archivo es de ${maxFileSize}MB`, { variant: "error" });
                    loopError = true;
                    break;
                }

                if (ignoreType?.includes(fileExtension)) {
                    alert("Documento no permitido");
                    loopError = true;
                    break;
                }
                //Good select file by file type specified on extension
                if (extension != undefined && extension?.length > 0) {
                    //Validate File extension valid in constands types and fileType in file string name example file.pdf
                    if (types.find((type) => extension.includes(type)) && extension.includes(fileExtension)) {
                        setSelectedFileName(file.name);
                        data.push(file);
                        continue;
                    } else {
                        //bad
                        setSelectedFileName("");
                        alert(`El archivo requiere una extension .${extension}`);
                        loopError = true;
                        break;
                    }
                }
                //Good select valid file
                if (types.find((type) => type === fileExtension)) {
                    setSelectedFileName(file.name);
                    data.push(file);
                    continue;
                }
                //bad
                setSelectedFileName("");
                alert("Documento no permitido");
                loopError = true;
                break;
            }

            if (loopError == false) {
                //Good
                let concatData = [];
                if (multipleDocuments === false) {
                    setSelectedItemsByIndexInDocuments([]);
                    concatData = [...data];
                } else {
                    concatData = [...selectedFiles, ...data];
                }
                setSelectedFiles(concatData);
                action({
                    target: {
                        id,
                        value: {
                            files: concatData,
                            selectedDocumentsArray: selectedItemsByIndexInDocuments,
                        },
                    },
                });
            } else {
                //Bad
            }
        }
    };
    const handleDocumentSelect = (e, index) => {
        if (multipleDocuments === false && selectedFiles.length >= 1 && selectedItemsByIndexInDocuments.length >= 1) {
            enqueueSnackbar("No se puede agregar mas de un documento", { variant: "error" });
            return;
        }

        if (extension != undefined && extension?.length > 0) {
            if (types.find((type) => extension.includes(type)) && extension.includes(e.type)) {
                //Good check selected file extension is equal than field required extension
            } else {
                //bad
                setSelectedFileName("");
                alert(`El archivo requiere una extension .${extension}`);
                return;
            }
        }
        //Good select a valid file
        setSelectedItemsByIndexInDocuments((prev) => [...prev, e.id]);
        setSelectedFileName(e.name);
        enqueueSnackbar("Documento agregado", { variant: "success" });
        if (multipleDocuments == false) {
            handleMyDocumentsModalVisibility();
        }

        let concatData = [];
        if (multipleDocuments === true) {
            concatData = [
                ...selectedFiles,
                {
                    id: e.id,
                    indexInDocumentsArray: index,
                    isARoute: true,
                    name: e.nameClear,
                    extension: e.type,
                    type: e.type,
                    route: e.route,
                    size_mb: e.size_mb,
                },
            ];
        } else {
            setSelectedItemsByIndexInDocuments([e.id]);
            concatData = [
                {
                    id: e.id,
                    indexInDocumentsArray: index,
                    isARoute: true,
                    name: e.nameClear,
                    extension: e.type,
                    type: e.type,
                    route: e.route,
                    size_mb: e.size_mb,
                },
            ];
        }
        setSelectedFiles(concatData);
        onChange({
            target: {
                id,
                value: {
                    files: concatData,
                    selectedDocumentsArray: selectedItemsByIndexInDocuments,
                },
            },
        });
    };

    const handleRemoveFileFromSelectedFiles = (file, position) => {
        enqueueSnackbar("Documento removido", { variant: "success" });
        setSelectedFileName("");
        let newSelectedItemsByIndexInDocuments = selectedItemsByIndexInDocuments;
        if (file?.isARoute) {
            newSelectedItemsByIndexInDocuments = selectedItemsByIndexInDocuments.filter((id) => id !== file.id);
            setSelectedItemsByIndexInDocuments(newSelectedItemsByIndexInDocuments);
        }
        let newFiles = [];
        selectedFiles.forEach((item, index) => {
            if (item.id != file.id) {
                newFiles.push(item);
            }
        });
        setSelectedFiles(newFiles);
        onChange({
            target: {
                id,
                value: {
                    files: newFiles,
                    selectedDocumentsArray: newSelectedItemsByIndexInDocuments,
                },
            },
        });
    };

    const handleMyDocumentsModalVisibility = () => {
        setMyDocumentsModalIsOpen(!myDocumentsModalIsOpen);
    };

    const handleSelectedFilesModalVisibility = () => {
        setSelectedFilesModalIsOpen(!selectedFilesModalIsOpen);
    };

    const documentsDataForShow = documentsData?.data?.map((document) => {
        return {
            id: document.id,
            isARoute: true,
            name: `${document.name}.${document.extension}`,
            nameClear: document.name,
            documentType: document.extension,
            date: format(new Date(replaceGuionToSlashFromString(document.created_at)), "yyyy-MM-dd"),
            url: document.url,
            type: document.extension,
            extension: document.extension,
            route: document.route,
            size_mb: document.size_mb || 0,
        };
    });

    const selectedFilesDataForShow = selectedFiles?.map((file) => {
        return {
            id: file.id,
            isARoute: file?.isARoute,
            indexInDocumentsArray: file?.indexInDocumentsArray,
            name: `${file.name}${file?.isARoute ? `.${file.extension}` : ""} `,
            nameClear: "",
            documentType: "",
            date: format(new Date(), "yyyy-MM-dd"),
            url: "",
            type: "",
            extension: file.extension,
            route: file?.route,
            size_mb: file.size_mb || 0,
        };
    });

    const handleChangePage = (page) => {
        setCurrentPage(page);
    };

    if (documentsDataLoading) return null;
    return (
        <Container>
            <Row style={{ width: '100%', marginBottom: '10px' }}>
                <FieldTitle sx={{ display: "flex", flexDirection: "row", alignItems: "flex-start", height: 'auto', minHeight: 'auto', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: '14px', color: '#192873', lineHeight: 1.45, wordBreak: 'break-word' }}>
                    {title} {required ? <span style={{ color: COLORS.red, paddingLeft: 4 }}>*</span> : null}
                </FieldTitle>
            </Row>

            {/* Hidden native input */}
            <InputFile
                id={id}
                type="file"
                value={""}
                multiple={multipleDocuments}
                onChange={(e) => {
                    validateAndChangeSelectedFiles(onChange, e);
                }}
            />

            {multipleDocuments ? (
                // ── MULTIPLE DOCUMENTS LAYOUT ──
                <div style={{ width: '100%' }}>
                    <DropZoneContainer
                        isDragging={isDragging}
                        disabled={disabled}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={handleDropZoneClick}
                    >
                        <CloudUploadIcon sx={{ color: isDragging ? COLORS.primary : COLORS.secondary, fontSize: '48px', transition: 'color 0.2s' }} />
                        <DropZoneTitle>
                            Arrastre y suelte sus archivos aquí, o <span style={{ color: COLORS.primary, textDecoration: 'underline', fontWeight: 'bold' }}>busque en su dispositivo</span>
                        </DropZoneTitle>
                        <DropZoneSubtitle>
                            {extension ? `Formatos permitidos: .${Array.isArray(extension) ? extension.join(', .') : extension}` : 'Formatos permitidos: Documentos y archivos generales'} (máx. {maxFileSize}MB por archivo)
                        </DropZoneSubtitle>

                        {findDocuments && (
                            <StyledButton
                                variant="outlined"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleMyDocumentsModalVisibility();
                                }}
                                style={{ marginTop: '16px', padding: '6px 16px', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                            >
                                <FolderSharedOutlinedIcon sx={{ fontSize: '18px' }} /> Seleccionar de Mis Documentos
                            </StyledButton>
                        )}
                    </DropZoneContainer>

                    {/* Selected files list */}
                    {selectedFiles.length > 0 && (
                        <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 4px 0' }}>
                                <span style={{ fontSize: '13px', fontWeight: '600', color: '#64748B' }}>
                                    Archivos seleccionados ({selectedFiles.length})
                                </span>
                                <Link
                                    component="button"
                                    variant="body2"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleSelectedFilesModalVisibility();
                                    }}
                                    sx={{ color: COLORS.secondary, fontWeight: '600', fontSize: '13px', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                    Ver detalle
                                </Link>
                            </div>
                            {selectedFiles.map((file, idx) => (
                                <FileCardContainer key={file.id || idx}>
                                    <FileCardInfo>
                                        {getFileIcon(file.name)}
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                            <FileCardName>{file.name}{file?.isARoute ? `.${file.extension}` : ''}</FileCardName>
                                            <FileCardSize>
                                                {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : file.size_mb ? `${file.size_mb} MB` : 'N/D'}
                                            </FileCardSize>
                                        </div>
                                    </FileCardInfo>
                                    <FileCardActionWrapper>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                handleRemoveFileFromSelectedFiles(file, idx);
                                            }}
                                            sx={{ p: '6px' }}
                                        >
                                            <DeleteOutlineIcon sx={{ fontSize: '20px' }} />
                                        </IconButton>
                                    </FileCardActionWrapper>
                                </FileCardContainer>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                // ── SINGLE DOCUMENT LAYOUT ──
                <div style={{ width: '100%' }}>
                    {selectedFiles.length > 0 ? (
                        /* Single selected file card */
                        <FileCardContainer style={{ marginTop: 0, padding: '16px', border: '1px solid #CBD5E1', borderLeft: `4px solid ${COLORS.secondary}` }}>
                            <FileCardInfo>
                                {getFileIcon(selectedFiles[0].name)}
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <FileCardName style={{ fontSize: '14px', fontWeight: '600' }}>
                                        {selectedFiles[0].name}{selectedFiles[0]?.isARoute ? `.${selectedFiles[0].extension}` : ''}
                                    </FileCardName>
                                    <FileCardSize>
                                        {selectedFiles[0].size ? `${(selectedFiles[0].size / 1024 / 1024).toFixed(2)} MB` : selectedFiles[0].size_mb ? `${selectedFiles[0].size_mb} MB` : 'N/D'}
                                    </FileCardSize>
                                </div>
                            </FileCardInfo>
                            <FileCardActionWrapper>
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleRemoveFileFromSelectedFiles(selectedFiles[0], 0);
                                    }}
                                    sx={{ p: '8px' }}
                                >
                                    <DeleteOutlineIcon sx={{ fontSize: '22px' }} />
                                </IconButton>
                            </FileCardActionWrapper>
                        </FileCardContainer>
                    ) : (
                        /* Empty drop zone */
                        <DropZoneContainer
                            isDragging={isDragging}
                            disabled={disabled}
                            onDragEnter={handleDragEnter}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={handleDropZoneClick}
                            style={{ borderColor: error ? COLORS.red : undefined }}
                        >
                            <CloudUploadIcon sx={{ color: isDragging ? COLORS.primary : COLORS.secondary, fontSize: '48px', transition: 'color 0.2s' }} />
                            <DropZoneTitle>
                                Arrastre y suelte su archivo aquí, o <span style={{ color: COLORS.primary, textDecoration: 'underline', fontWeight: 'bold' }}>busque en su dispositivo</span>
                            </DropZoneTitle>
                            <DropZoneSubtitle>
                                {extension ? `Formatos permitidos: .${Array.isArray(extension) ? extension.join(', .') : extension}` : 'Formatos permitidos: Documentos y archivos generales'} (máx. {maxFileSize}MB)
                            </DropZoneSubtitle>

                            {findDocuments && (
                                <StyledButton
                                    variant="outlined"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleMyDocumentsModalVisibility();
                                    }}
                                    style={{ marginTop: '16px', padding: '6px 16px', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                                >
                                    <FolderSharedOutlinedIcon sx={{ fontSize: '18px' }} /> Seleccionar de Mis Documentos
                                </StyledButton>
                            )}
                        </DropZoneContainer>
                    )}
                    {helperText && (
                        <FormHelperText error={error} sx={{ fontSize: "0.85rem", marginTop: '4px', marginLeft: '4px' }}>
                            {helperText}
                        </FormHelperText>
                    )}
                </div>
            )}

            {/* Modals are kept unchanged for compatibility */}
            <FormModal open={selectedFilesModalIsOpen} onClose={handleSelectedFilesModalVisibility} title={`${title} - Archivos`}>
                <DocumentsOfRequestsCard
                    data={selectedFilesDataForShow}
                    disableCardStyle
                    hideSeeButton
                    hideDownloadButton
                    showDeleteButton
                    onDeleteClick={handleRemoveFileFromSelectedFiles}
                />
                <SmallHeightDivider />
            </FormModal>
            <FormModal open={myDocumentsModalIsOpen} onClose={handleMyDocumentsModalVisibility} title="Mis documentos">
                <div style={{ maxHeight: '58vh', overflowY: 'auto', paddingRight: '6px', paddingBottom: '8px' }}>
                    <DocumentsOfRequestsCard
                        data={documentsDataForShow}
                        disableCardStyle
                        showSelectButton
                        hideDownloadButton={hideDownloadButton}
                        onSelectClick={handleDocumentSelect}
                        onDeleteClick={handleRemoveFileFromSelectedFiles}
                        selectedItemsByIndex={selectedItemsByIndexInDocuments}
                    />
                </div>
                <PaginationContainer style={{ marginTop: '16px' }}>
                    <StyledPagination
                        count={documentsData?.last_page}
                        page={currentPage}
                        onChange={(event, page) => {
                            handleChangePage(page);
                        }}
                        variant="outlined"
                        shape="rounded"
                        sx={{ color: COLORS.primary }}
                    />
                    <SmallHeightDivider />
                </PaginationContainer>
                <SmallHeightDivider />
            </FormModal>
        </Container>
    );
}

export default memo(UploadFile);
